import * as React from 'react';
import Alert from '@mui/material/Alert';
import { DataGrid } from '@mui/x-data-grid';
import { columns } from '../internal/data/gridData';
import { canViewTransactions } from '../../helpers/authSession';
import { useSelector } from 'react-redux';
import { getApiDateRangeFromFilterState } from '../../helpers/transactionMetrics';
import { rulesEngineGet } from '../../services/rulesEngineClient';
import { getRulesEngineTransactionsUrl } from '../../services/rulesEngineEndpoints';

function normalizeStatus(value) {
  if (!value)
    return 'Aguardando Análise';

  const raw = String(value).trim().toUpperCase();

  if (raw === 'FINALIZED' || raw === 'FINALIZADO' || raw === 'FINALIZADA')
    return 'Finalizado';

  if (raw === 'REPORT' || raw === 'REPORTED' || raw === 'REPORTADO' || raw === 'REPORTADA')
    return 'Reportado';

  if (raw === 'WAITING_ANALYSIS' || raw === 'AGUARDANDO_ANALISE')
    return 'Aguardando Análise';

  if (raw === 'ANALYSIS' || raw === 'ANALISE' || raw === 'EM_ANALISE')
    return 'Em Análise';

  return 'Aguardando Análise';
}

export default function CustomizedDataGrid() {
  const canView = canViewTransactions();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const filterState = useSelector((state) => state.filter);
  const dateRange = React.useMemo(() => getApiDateRangeFromFilterState(filterState), [filterState]);

  React.useEffect(() => {
    if (!canView) {
      setRows([]);
      setLoading(false);
      return undefined;
    }

    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const rulesUrl = process.env.REACT_APP_API_RULES_URL;

        if (!rulesUrl) {
          setRows([]);
          return;
        }

        const responseData = await rulesEngineGet(getRulesEngineTransactionsUrl(rulesUrl), {
          params: {
            start_date: dateRange.startDate,
            end_date: dateRange.endDate,
          },
        });

        const transactions = responseData.transactions || responseData;

        if (!Array.isArray(transactions)) {
          setRows([]);
          return;
        }

        const formattedData = transactions.map((item) => ({
          id: item.operation_id || item.id,
          status: normalizeStatus(item.analysis_status || item.status || item.operation_status || item.transaction_status),
          value: `R$ ${item.value}`,
          transactionType: item.transaction_type,
          eventCount: item.alerts_summary?.total_alerts || 0,
          dateHourTransaction: item.transaction_datetime
            ? new Date(item.transaction_datetime).toLocaleString('pt-BR')
            : '',
          appointments: item.alerts_summary?.alerts?.map((alert) => alert.description) || [],
        }));

        setRows(formattedData);
      } catch (error) {
        console.error('Erro ao buscar transações:', error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [canView, dateRange.endDate, dateRange.startDate]);

  if (!canView) {
    return <Alert severity="warning">Seu perfil não possui permissão para visualizar transações.</Alert>;
  }

  return (
    <DataGrid
      checkboxSelection
      loading={loading}
      rows={rows}
      columns={columns}
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
      }
      initialState={{
        pagination: { paginationModel: { pageSize: 20 } },
      }}
      pageSizeOptions={[10, 20, 50]}
      disableColumnResize
      density="compact"
      slotProps={{
        filterPanel: {
          filterFormProps: {
            logicOperatorInputProps: {
              variant: 'outlined',
              size: 'small',
            },
            columnInputProps: {
              variant: 'outlined',
              size: 'small',
              sx: { mt: 'auto' },
            },
            operatorInputProps: {
              variant: 'outlined',
              size: 'small',
              sx: { mt: 'auto' },
            },
            valueInputProps: {
              InputComponentProps: {
                variant: 'outlined',
                size: 'small',
              },
            },
          },
        },
      }}
    />
  );
}
